import { Component, EventEmitter, Input, OnDestroy, DestroyRef, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BusinessTypeIDs, MemberAddress, ServiceLocationDTO } from '@core/model';
import { AuthenticationService, LookupService, ProviderService, TenantService } from '@core/service';
import { ProviderManagementService } from '@core/service/provider-management.service';
import { BusinessType, ProviderAccountPgpDTO, ProviderPgpDTO } from '@mynexus/mynexus-ui-lib';
import { MessageService, SelectItem } from 'primeng/api';
import { filter, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-auth-request-provider',
    templateUrl: './auth-request-provider.component.html',
    styleUrls: ['./auth-request-provider.component.scss'],
    standalone: false
})

// tslint:disable:max-line-length
export class AuthRequestProviderComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  @Output() isStepFormValid = new EventEmitter<boolean>();
  @Output() providerChange = new EventEmitter<ProviderAccountPgpDTO>();
  @Output() arrangeForAnotherAgencyChanged = new EventEmitter<boolean>();
  @Output() serviceLocationDTOChange = new EventEmitter<ServiceLocationDTO>();
  @Output() isAcceptingCareChange = new EventEmitter<any>();
  @Output() npiNotFoundProviderChange = new EventEmitter<boolean>();
  @Output() isProviderNotFoundChange = new EventEmitter<boolean>();
  @Output() networkListChange = new EventEmitter<ProviderPgpDTO[]>();
  @Output() isManuallySelectedNetworkChange = new EventEmitter<boolean>();
  @Input() requestType: string;
  @Input() isAcceptingCare = null;
  @Input() memberAddresses: MemberAddress[];
  @Input() npiNotFoundProvider = false;
  @Input() isProviderNotFound = false;
  @Input() serviceLocationDTO: ServiceLocationDTO = null;
  @Input() provider: any = null;
  @Input() networkList: ProviderPgpDTO[] = null;
  @Input() tenantName: string;
  @Input() isManuallySelectedNetwork: boolean;


  tenantId: string;
  isExternalUser: boolean;
  mailingAddress = 'Mailing';
  residentialAddress = 'Residential';
  mailingLivingArrangement = 'Mailing/Living Arrangement';

  isLoading = false;
  displayAcceptingCareQuestion = false;
  displayAnotherAgencyQuestion = false;

  providerServiceLocationForm: FormGroup;

  serviceAddressTypes: SelectItem[];
  serviceAddressLocationTypes: SelectItem[];

  providerList: any;
  isSelectViewMode = false;
  isSearchViewMode = true;

  haveProvider = false;
  showServProvContinueMessage = false;
  networks: ProviderPgpDTO[];
  isNetwork = false;
  constructor(
    private authenticationService: AuthenticationService,
    private providerService: ProviderService,
    private providerManagementService: ProviderManagementService,
    private lookupService: LookupService,
    private tenantService: TenantService,
    private messageService: MessageService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit() {
    // Get Global Values
    this.setGlobalVariables();

    // Get Lookups
    this.getLookups();

    if (this.isExternalUser) {
      this.getProviderListExternalUser();
    }

    // Build the Form
    this.buildProviderForms();

    this.handleFirstTimeStepEntry();

    // Form Subscriptions
    this.providerServiceLocationFormWatch();

    // Initial Form Validation Emit
    this.setDefaultValues();
  }

  ngOnDestroy() {
  }

  handleServiceProviderSelected($event: ProviderAccountPgpDTO[]) {
    this.providerServiceLocationForm.reset();
    this.isManuallySelectedNetworkChange.emit(false);
    this.providerChange.emit(null);

    if ($event?.length === 1) {
      this.provider = $event[0];
      this.providerChange.emit(this.provider);
      this.haveProvider = true;
      this.isNetwork = false;
      this.networkListChange.emit(this.provider.providerPgpDTOList);
    } else {
      this.providerList = $event;
    }
    this.isSelectViewMode = $event?.length <= 1  ? false : true;

    this.isProviderNotFound = !$event?.length;

    this.npiNotFoundProviderChange.emit(false);
    this.serviceLocationDTOChange.emit(this.providerServiceLocationForm.value);
  }

  handleNpiNotFound(event: boolean) {
    this.npiNotFoundProviderChange.emit(event);
    this.npiNotFoundProvider = event;
    this.isNetwork = event;
    this.setFormValidity();
  }

  handleProviderNpiNotFound(event: boolean) {
    this.isProviderNotFoundChange.emit(event);
    this.isProviderNotFound = event;
  }

  switchToProviderDetail($event) {
    if (this.isExternalUser && this.isAcceptingCare) {
      this.getServiceProvider($event);
      this.getNetworksList($event);
    } else {
      this.provider = $event;
      this.providerChange.emit(this.provider);
      this.haveProvider = true;
      this.networkListChange.emit(this.provider.providerPgpDTOList);
      this.isSelectViewMode = false;
      this.isNetwork = false;
      this.setFormValidity();
    }
  }

  getNetworksList(provider) {
    return this.providerManagementService.getProviderActivePgpsByProviderAccountId(provider.providerAccountId, this.tenantId).subscribe(resp => {
      this.networks = resp;
    });
  }

  getServiceProvider(provider) {
    return this.providerManagementService.getProviderAccountPgpByAccountIdPgpId(provider.providerAccountId, provider.providerPgpId, this.tenantId)
    .subscribe(resp => {
      this.provider = resp;
      this.haveProvider = true;
      this.isSelectViewMode = false;
      this.isProviderNotFound = false;
      this.providerChange.emit(this.provider);
    });
  }


  setGlobalVariables() {
    this.tenantId = this.route.snapshot.params.tenantId;
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  setDefaultValues() {
    if (!this.provider) {
      this.isNetwork = false;
      this.setFormValidity();
    }
    else {
      this.isNetwork = true;
      this.isProviderNotFound = false;
      const valid = this.providerServiceLocationForm.valid && this.isNetwork;
      this.isStepFormValid.emit(valid);
    }
  }

  handleFirstTimeStepEntry() {
    if (this.isExternalUser) {
      this.handleExternalUserFirstStepEntry();
    }
  }

  handleUpdateProvider(event) {
    this.isNetwork = true;
    this.setFormValidity();
    this.providerChange.emit(event);
  }

  handleExternalUserFirstStepEntry() {
    this.tenantService.getUserBusinessType(true)
    .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((businessType: BusinessType) => {
        if (businessType.businessTypeID === BusinessTypeIDs.HealthPlanCaseManager ||
            businessType.businessTypeID === BusinessTypeIDs.Physician ||
            businessType.businessTypeID === BusinessTypeIDs.HospitalCMDischargePlanner) {
          this.isAcceptingCareChange.emit(null);
          this.handleIsAcceptingCare(false);
          this.showServProvContinueMessage = true;
        } else {
          this.showAcceptingCareQuestion();
        }
      }
    );
  }

  buildProviderForms() {
    this.buildProviderServiceLocationForm();
  }

  buildProviderServiceLocationForm() {
    this.providerServiceLocationForm = new FormGroup({
      serviceAddressType: new FormControl(this.serviceLocationDTO.serviceAddressType, Validators.required),
      serviceAddressLocationType: new FormControl(this.serviceLocationDTO.serviceAddressLocationType, Validators.required),
      serviceAddress1: new FormControl(this.serviceLocationDTO.address1, [Validators.required, Validators.minLength(3), Validators.maxLength(150)]),
      serviceAddress2: new FormControl(this.serviceLocationDTO.address2, [Validators.maxLength(150)]),
      serviceCity: new FormControl(this.serviceLocationDTO.city, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
      serviceState: new FormControl({value: this.serviceLocationDTO.state, disabled: true}, [Validators.required, Validators.minLength(2), Validators.maxLength(25)]),
      serviceZipcode: new FormControl(this.serviceLocationDTO.zipCode, [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]+')]),
      serviceCounty: new FormControl({value: this.serviceLocationDTO.county, disabled: true}, [Validators.minLength(3), Validators.maxLength(30)])
    });
  }


  providerServiceLocationFormWatch(): void {
    const countyControl = this.providerServiceLocationForm.get('serviceCounty');
    const stateControl = this.providerServiceLocationForm.get('serviceState');
    const zipcodeControl = this.providerServiceLocationForm.get('serviceZipcode');

    this.providerServiceLocationForm.get('serviceZipcode').valueChanges.pipe(
      tap(() => {
        countyControl.setValue('');
        stateControl.setValue('');
      }),
      filter(() =>  zipcodeControl.valid),
      switchMap(zipCode => this.providerService.getZipCodeCountyState(zipCode))
    )
    .subscribe(({ state, county, zipCode }) => {
      if (!zipCode) {
        this.messageService.add({
          severity: 'error',
          detail: `ZipCode ${zipcodeControl.value} Not Found.`,
          life: 5000
        });
      } else {
        stateControl.setValue(state);
        countyControl.setValue(county);
      }
      this.providerServiceLocationForm.updateValueAndValidity();
    });

    this.providerServiceLocationForm.valueChanges.subscribe(val => {
      const serviceData = val;

      serviceData.serviceCounty = countyControl.value;
      serviceData.serviceState = stateControl.value;
      this.loadServiceLocationFieldChanges(serviceData);
    });
  }

  loadServiceLocationFieldChanges(formValue) {
    // ServiceLocationDTO
    this.serviceLocationDTO.serviceAddressType = formValue.serviceAddressType;
    this.serviceLocationDTO.serviceAddressLocationType = formValue.serviceAddressLocationType;
    this.serviceLocationDTO.address1 = formValue.serviceAddress1;
    this.serviceLocationDTO.address2 = formValue.serviceAddress2;
    this.serviceLocationDTO.city = formValue.serviceCity;
    this.serviceLocationDTO.state = formValue.serviceState;
    this.serviceLocationDTO.zipCode = formValue.serviceZipcode;
    this.serviceLocationDTO.county = formValue.serviceCounty;
    this.serviceLocationDTOChange.emit(this.serviceLocationDTO);
    this.setFormValidity();
  }

  getLookups() {
    this.getServiceAddressTypes();
    this.getServiceAddressLocationTypes();
  }

  getServiceAddressTypes() {
    this.lookupService.getServiceAddressTypes()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(resp => {
      this.serviceAddressTypes = resp.map(r => ({ value: r.serviceAddressType, label: r.serviceAddressType } as SelectItem));
    });
  }

  getServiceAddressLocationTypes() {
    this.lookupService.getServiceAddressLocationTypes()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(resp => {
      this.serviceAddressLocationTypes = resp.map(r => ({ value: r.serviceAddressLocationType, label: r.serviceAddressLocationType } as SelectItem));
    });
  }


  setFormValidity() {
    const isValid = this.isExternalUser ?
      this.showServProvContinueMessage
      ? true
      : ((this.providerServiceLocationForm.valid && this.isNetwork && this.provider) || (this.npiNotFoundProvider && this.providerServiceLocationForm.valid ))
    : ((this.providerServiceLocationForm.valid && this.isNetwork && this.provider) || (this.npiNotFoundProvider && this.providerServiceLocationForm.valid ));

    this.isStepFormValid.emit(isValid);
  }


  // Provider List for External User ,takes less than a second
  getProviderListExternalUser() {
    this.providerManagementService.getProviderListExternalUser(this.authenticationService.oid, this.tenantId).subscribe(
      resp => {
        this.providerList = resp;
      },
      () => {
        console.error('Error: Unable to retrieve ProviderList for External User');
      }
    );
  }

  showAcceptingCareQuestion() {
    this.isSearchViewMode = false;
    this.displayAcceptingCareQuestion = true;
  }

  handleIsAcceptingCareResponse(resp: boolean) {
    this.isAcceptingCare = resp;
    this.handleIsAcceptingCare(resp);
    if (!resp) {
      this.displayAnotherAgencyQuestion = true;
    }

    this.isAcceptingCareChange.emit(resp);
    this.providerChange.emit(null);
    this.providerServiceLocationForm.reset();
    this.setFormValidity();
  }

  handleIsAcceptingCare(resp: boolean) {
    this.isSelectViewMode = resp;
    this.isSearchViewMode = false;
    this.showServProvContinueMessage = false;
    this.haveProvider = false;
    this.isNetwork = false;
    this.isManuallySelectedNetworkChange.emit(false);
    this.setFormValidity();
    this.providerChange.emit(null);
    this.providerServiceLocationForm.reset();
  }

  handleIsAnotherAgencyResponse(resp: boolean) {
    this.arrangeForAnotherAgencyChanged.emit(resp);
    this.isSelectViewMode = false;
    this.haveProvider = false;
    if (resp) {
      this.showServProvContinueMessage = false;
      this.isSearchViewMode = true;
    } else {
      this.showServProvContinueMessage = true;
      this.isSearchViewMode = false;
    }
    this.providerChange.emit(null);
    this.serviceLocationDTOChange.emit(this.providerServiceLocationForm.value);
    this.setFormValidity();
  }

  loadMemberServiceLocationAddress(event) {
    if (this.memberAddresses && this.memberAddresses.length > 0) {
      const hasNeededAddress = this.memberAddresses.findIndex(m => m.addressType === this.residentialAddress || m.addressType === this.mailingAddress) >= 0;
      if (hasNeededAddress) {
        const memberResidential = this.memberAddresses.find(m => m.addressType === this.residentialAddress);
        const memberMailing = this.memberAddresses.find(m => m.addressType === this.mailingAddress);
        let address = new MemberAddress();
        if (String(event.value) === this.residentialAddress && memberResidential) {
          address = memberResidential;
        } else if (String(event.value) === this.mailingLivingArrangement && memberMailing) {
          address = memberMailing;
        }
        this.providerServiceLocationForm.get('serviceAddress1').setValue(address.address1);
        this.providerServiceLocationForm.get('serviceAddress2').setValue(address.address2);
        this.providerServiceLocationForm.get('serviceCity').setValue(address.city);
        this.providerServiceLocationForm.get('serviceState').setValue(address.state);
        this.providerServiceLocationForm.get('serviceZipcode').setValue(address.zipCode);
        this.providerServiceLocationForm.get('serviceCounty').setValue(address.county);
      } else {
        this.messageService.add({ severity: 'info', summary: 'No Residential/Mailing Address', detail: 'Member Does Not Have Either A Residential Or Mailing Address' });
      }
    } else {
      this.messageService.add({ severity: 'info', summary: 'No Address', detail: 'Member Does Not Have Any Addresses' });
    }
  }
}
